import { DefaultChatTransport } from 'ai';

import type { LeDataAskOrigin, LeDataContextBlock } from '~/services/ReportingAiAskService';

import type { LensIntent, MantleMode } from '~/types/leData';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { SseHttpError } from '~/utils/sse/fetchSsePost';

import type { AskUIMessage } from './askUiMessage';
import { imageAttachmentOf, userImageParts } from './imageParts';
import { userMessageText } from './projectTurn';

export const UI_MESSAGE_STREAM_WIRE = 'ui-message-stream';

export interface AskTurnOptions {
  sessionId?: string | null;
  mode?: MantleMode;
  origin?: LeDataAskOrigin;
  lensId?: string;
  lensIntent?: LensIntent;
  context?: ReadonlyArray<LeDataContextBlock>;
}

export function askRequestBody(
  messages: ReadonlyArray<AskUIMessage>,
  turn: AskTurnOptions | undefined,
): Record<string, unknown> {
  const sessionId = turn?.sessionId ?? '';
  const isNewSession = !sessionId;
  const lastUser = [...messages].reverse().find((message) => message.role === 'user');
  const images = lastUser ? userImageParts(lastUser).flatMap((part) => imageAttachmentOf(part) ?? []) : [];
  return {
    wire: UI_MESSAGE_STREAM_WIRE,
    turnId: crypto.randomUUID(),
    question: lastUser ? userMessageText(lastUser) : '',
    sessionId,
    ...(isNewSession && turn?.mode ? { mode: turn.mode } : {}),
    ...(isNewSession && turn?.origin ? { origin: turn.origin } : {}),
    ...(turn?.lensId ? { lensId: turn.lensId } : {}),
    ...(turn?.lensIntent ? { lensIntent: turn.lensIntent } : {}),
    ...(turn?.context?.length ? { context: turn.context } : {}),
    ...(images.length ? { images } : {}),
  };
}

async function httpErrorFrom(response: Response): Promise<SseHttpError> {
  const text = (await response.text().catch(() => '')).trim().slice(0, 400);
  let detail: string;
  let code: string | undefined;
  try {
    const parsed = JSON.parse(text) as { message?: unknown; error?: unknown; code?: unknown };
    detail =
      (typeof parsed.message === 'string' && parsed.message) ||
      (typeof parsed.error === 'string' && parsed.error) ||
      text;
    code = typeof parsed.code === 'string' ? parsed.code : undefined;
  } catch {
    detail = text;
  }
  const suffix = detail ? `: ${detail}` : '';
  return new SseHttpError(`HTTP ${response.status}${suffix}`, response.status, detail, code);
}

export function rejectingNonOkFetch(fetchImpl: typeof fetch): typeof fetch {
  return async (input, init) => {
    const response = await fetchImpl(input, init);
    if (!response.ok) {
      throw await httpErrorFrom(response);
    }
    return response;
  };
}

export interface AskTransportConfig {
  api: string;
  getAccessToken: () => Promise<string | null | undefined>;
  fetchImpl?: typeof fetch;
}

export function createAskTransport({ api, getAccessToken, fetchImpl }: AskTransportConfig) {
  return new DefaultChatTransport<AskUIMessage>({
    api,
    credentials: 'include',
    headers: async () => {
      const token = await getAccessToken();
      if (!token) {
        throw Object.assign(new Error('You are not logged in.'), { name: 'AuthorizationError' });
      }
      return { Authorization: `Bearer ${token}` };
    },
    fetch: rejectingNonOkFetch(fetchImpl ?? ((input, init) => globalThis.fetch(input, init))),
    prepareSendMessagesRequest: ({ messages, body }) => ({
      body: encodeRequestBody(askRequestBody(messages, body as AskTurnOptions | undefined)),
    }),
  });
}
